<template>
    <form ref="formElement" @submit.prevent="submit">
        <div v-if="errorMessage" class="alert alert-danger mt-1">{{ errorMessage }}</div>
        <slot></slot>
    </form>
</template>

<script lang="ts" setup>
import type {Dictionary} from "../types.ts";
import {computed, inject, onMounted, ref, watch} from "vue";
import axios, {type AxiosError} from "axios";
import {WidgetInstance} from "friendly-challenge";
import type Provider from "../provider.ts";

interface Props {
    routes: Dictionary<string>
    slug: string
}

const {routes, slug} = defineProps<Props>();

const {
    semaphore,
    dropzoneErrorState
} = (inject('Provider') as Provider).refs;

axios.defaults.headers.common['X-Requested-With'] = 'XMLHttpRequest';

const formElement = ref<HTMLFormElement>();
let submitButtonElement: null | HTMLButtonElement = null;

const errorMessage = ref<false | string>(false);

const submitButtonDisabled = computed<boolean>(() => {
    return semaphore.value > 0 || dropzoneErrorState.hasUploadError;
});

watch(submitButtonDisabled, value => {
    if(!submitButtonElement) {
        return;
    }

    submitButtonElement.disabled = value;
});

onMounted(() => {
    submitButtonElement = formElement.value!.querySelector('button[type="submit"]');
})

const submit = () => {

    semaphore.value++;
    errorMessage.value = false;

    const formData = new FormData(formElement.value);

    axios.post(routes.apply + slug, formData)
        .then(response => {
            if (document.getElementById('step1')) {
                document.getElementById('step1')?.classList.add('d-none');
            }
            if (document.getElementById('step2')) {
                document.getElementById('step2')?.classList.remove('d-none');
            }

            //dataLayer push if cookieBanner is active
            if (dataLayerApplyWithSource !== undefined && (typeof dataLayerApplyWithSource === 'function')) {
                dataLayerApplyWithSource();
            }
        })
        .catch((error: AxiosError) => {
            clearValidation();
            if (error.response) {
                if (error.response.status === 422) {
                    Object.entries(error.response.data as Dictionary<string>).forEach(([name, message]) => {
                        const inputElement = formElement.value?.querySelector(`input[name=${name}], textarea[name=${name}], select[name=${name}]`);
                        if (inputElement) {
                            inputElement.classList.add('is-invalid');
                            const invalidFeedbackElement = inputElement.parentElement?.querySelector('div.invalid-feedback');
                            if (invalidFeedbackElement) {
                                invalidFeedbackElement.innerHTML = message;
                            }
                        }
                    })
                } else {
                    errorMessage.value = 'Ein Fehler ist aufgetreten, versuche es später erneut.'
                }
            }
            formElement.value?.scrollIntoView({
                block: "start",
                behavior: "smooth"
            });
        })
        .finally(() => {
            semaphore.value--;
        });
}


const clearValidation = () => {
    const captchaElement = formElement.value?.querySelector('.frc-captcha') as HTMLElement;
    new WidgetInstance(captchaElement).reset();

    const inputElements = formElement.value?.querySelectorAll('input[name], textarea[name], select[name]');
    inputElements?.forEach(node => {
        const element = node as HTMLInputElement;
        element.classList.remove('is-invalid');
    });

    // remove style-tag inserted by reset-method
    document.querySelector('style#frc-style')?.remove();
}

</script>
